import { GridCol, GridContainer, StickyBar } from '@bob/library'
import { mapProductPage } from '@websrc/DataMappers/mapProductPage'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ProductInfoButtons } from '../Buttons/ProductInfoButtons'
import { ProductInfoPrice } from '../Price/ProductInfoPrice'
import { addToCartBar } from './AddToCartBar.css'

export function AddToCartBar({
  addToCartButtonRef,
  productDetails,
  quantity,
}: {
  addToCartButtonRef: React.RefObject<HTMLButtonElement>
  productDetails: ReturnType<typeof mapProductPage>['product_details']
  quantity: number
}) {
  const [isAddToCartButtonIntersecting, setIsAddToCartButtonIntersecting] = useState(true)
  const [sticksToBottom, setSticksToBottom] = useState(false)
  const stickyBarPortalEl = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsAddToCartButtonIntersecting(true)
          } else {
            setIsAddToCartButtonIntersecting(false)
          }
        })
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
      }
    )

    const buttonEl = addToCartButtonRef.current

    if (buttonEl) {
      intersectionObserver.observe(buttonEl)

      return () => intersectionObserver.unobserve(buttonEl)
    }
  }, [addToCartButtonRef])

  useEffect(() => {
    stickyBarPortalEl.current = document.getElementById('stickyBarPortal')

    const mediaQuery = window.matchMedia('(max-width: 1024px)')

    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        setSticksToBottom(true)
      } else {
        setSticksToBottom(false)
      }
    }

    mediaQuery.addEventListener('change', handleMediaChange)

    handleMediaChange(mediaQuery)

    return () => mediaQuery.removeEventListener('change', handleMediaChange)
  }, [])

  const stickyBar = (
    <div className={addToCartBar.row}>
      <div className={addToCartBar.detailsCol}>
        <div className={addToCartBar.productName}>{productDetails.name}</div>
        <ProductInfoPrice productDetails={productDetails} show={true} textSize={'xs'} />
      </div>
      <div className={addToCartBar.buttonCol}>
        <ProductInfoButtons
          color={'blue'}
          colorMode={'light'}
          productDetails={productDetails}
          quantity={quantity}
          size={'medium'}
          type={'primary'}
        />
      </div>
    </div>
  )

  if (!sticksToBottom && stickyBarPortalEl.current) {
    return createPortal(
      <StickyBar
        bgColor={'grayWhite'}
        className={addToCartBar.stickyBar}
        noContainer={false}
        // we set visibility here to have the element rendered even if it's not visible, to fix jumping on safary in the moment we show the sticky bar
        style={{ visibility: isAddToCartButtonIntersecting ? 'hidden' : 'visible' }}
      >
        {stickyBar}
      </StickyBar>,
      stickyBarPortalEl.current
    )
  } else if (!isAddToCartButtonIntersecting && sticksToBottom && stickyBarPortalEl.current) {
    return createPortal(
      <div className={addToCartBar.bottom}>
        <GridContainer>
          <GridCol>{stickyBar}</GridCol>
        </GridContainer>
      </div>,
      stickyBarPortalEl.current
    )
  }

  return null
}
